import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { IndianRupee, TrendingUp, AlertCircle, CheckCircle2, FileText, Loader2, Power, PlayCircle, Ban, AlertTriangle } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const STATUS = {
  active: "bg-status-available/15 text-status-available",
  past_due: "bg-status-overdue/15 text-status-overdue",
  suspended: "bg-status-recovery/15 text-status-recovery",
  cancelled: "bg-muted text-muted-foreground",
};
const inr = (v) => `₹${(v || 0).toLocaleString("en-IN")}`;

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
      <div className="flex items-center gap-2 text-sm text-muted-foreground"><Icon className="h-4 w-4" /> {label}</div>
      <p className={`mt-2 text-3xl font-semibold tracking-tight ${accent || ""}`}>{value}</p>
    </div>
  );
}

function InvoicesDialog({ sub, onChange }) {
  const [open, setOpen] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const { data } = await api.get(`/platform/billing/tenants/${sub.tenant_id}/invoices`);
    setInvoices(data);
  }, [sub.tenant_id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const generate = async () => {
    setBusy(true);
    try { await api.post(`/platform/billing/tenants/${sub.tenant_id}/invoices`); toast.success("Invoice generated"); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/platform/billing/invoices/${id}/mark-paid`, { method: "bank_transfer", reference: "" }); toast.success("Marked paid"); await load(); onChange?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`invoices-btn-${sub.subdomain}`} size="sm" variant="outline" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Invoices</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{sub.name} · billing</DialogTitle>
          <DialogDescription>{sub.plan_name} {inr(sub.plan_price)}/mo · this cycle would be {inr(sub.monthly_total)} (base + overages).</DialogDescription>
        </DialogHeader>
        <Button data-testid={`gen-invoice-${sub.subdomain}`} onClick={generate} disabled={busy} className="gap-2 bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} Generate invoice for this cycle
        </Button>
        <div className="space-y-2">
          {invoices === null ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> :
            invoices.length === 0 ? <p className="text-sm text-muted-foreground">No invoices yet.</p> :
            invoices.map((inv) => (
              <div key={inv.id} data-testid={`platform-invoice-${inv.id}`} className="rounded-md border border-border px-3 py-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{inv.number} · {inr(inv.total)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${inv.status === "paid" ? STATUS.active : inv.payment_submitted ? "bg-status-pending/15 text-status-pending" : inv.overdue ? STATUS.suspended : STATUS.past_due}`}>
                    {inv.status === "paid" ? "paid" : inv.payment_submitted ? "submitted" : inv.overdue ? "overdue" : "pending"}
                  </span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {inv.line_items.map((l) => `${l.label} ${inr(l.amount)}`).join(" · ")} · due {new Date(inv.due_date).toLocaleDateString()}
                </div>
                {inv.payment_submitted && inv.status !== "paid" && (
                  <p className="mt-1 text-xs text-status-pending">Operator submitted payment{inv.submitted_reference ? ` (ref ${inv.submitted_reference})` : ""} — verify & mark paid.</p>
                )}
                {inv.status !== "paid" && (
                  <Button data-testid={`mark-paid-${inv.id}`} size="sm" variant="ghost" className="mt-1.5 gap-1 text-status-available" onClick={() => markPaid(inv.id)}>
                    <CheckCircle2 className="h-3.5 w-3.5" /> Mark paid
                  </Button>
                )}
              </div>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PaymentConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/platform/billing/payment-config").then(({ data }) => setCfg(data)); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/platform/billing/payment-config", { upi_id: cfg.upi_id, payee_name: cfg.payee_name });
      setCfg(data); toast.success("Platform payment details saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!cfg) return null;
  return (
    <div data-testid="payment-config-card" className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
      <p className="font-semibold">Platform payment details</p>
      <p className="mt-0.5 text-sm text-muted-foreground">The UPI ID operators pay into (shown on their self-service billing page).</p>
      <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_1fr_auto]">
        <Input data-testid="platform-upi-input" value={cfg.upi_id || ""} onChange={(e) => setCfg({ ...cfg, upi_id: e.target.value })} placeholder="graphikpro@okhdfcbank" />
        <Input data-testid="platform-payee-input" value={cfg.payee_name || ""} onChange={(e) => setCfg({ ...cfg, payee_name: e.target.value })} placeholder="Payee name" />
        <Button data-testid="save-payment-config" onClick={save} disabled={busy} className="bg-tenant-primary text-tenant-primary-foreground hover:opacity-90">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function AutoInvoiceCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/platform/billing/auto-invoice-config").then(({ data }) => setCfg(data)); }, []);
  const save = async (enabled) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/platform/billing/auto-invoice-config", { auto_invoice_enabled: enabled });
      setCfg(data); toast.success(enabled ? "Auto-invoicing on" : "Auto-invoicing off");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  if (!cfg) return null;
  return (
    <div data-testid="auto-invoice-card" className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">Recurring auto-invoicing</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Raise each operator's monthly invoice automatically on their billing date (base + overages). Paused while suspended.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={cfg.auto_invoice_enabled ? "font-medium text-status-available" : "text-muted-foreground"}>Auto-invoice</span>
          <Switch data-testid="auto-invoice-toggle" disabled={saving} checked={cfg.auto_invoice_enabled}
            onCheckedChange={(v) => save(v)} />
        </div>
      </div>
    </div>
  );
}

function DunningCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const GRACE = [7, 14, 30];
  useEffect(() => { api.get("/platform/billing/dunning-config").then(({ data }) => setCfg(data)); }, []);

  const save = async (patch) => {
    setSaving(true);
    const next = { ...cfg, ...patch };
    try {
      const { data } = await api.patch("/platform/billing/dunning-config", next);
      setCfg(data); toast.success("Dunning settings saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  if (!cfg) return null;
  return (
    <div data-testid="dunning-card" className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">Automated dunning</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Overdue invoices flag operators as <span className="font-medium">past due</span> (banner shown). Auto-suspend locks them out after a grace period.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className={cfg.auto_suspend_enabled ? "font-medium text-status-recovery" : "text-muted-foreground"}>Auto-suspend</span>
          <Switch data-testid="dunning-autosuspend-toggle" disabled={saving} checked={cfg.auto_suspend_enabled}
            onCheckedChange={(v) => save({ auto_suspend_enabled: v })} />
        </div>
      </div>
      {cfg.auto_suspend_enabled && (
        <div className="mt-4 flex items-center gap-2 border-t border-border pt-4 text-sm">
          <span className="text-muted-foreground">Grace period after due date:</span>
          {GRACE.map((g) => (
            <button key={g} data-testid={`dunning-grace-${g}`} disabled={saving} onClick={() => save({ grace_days: g })}
              className={`rounded-md border px-3 py-1 text-sm transition-colors ${cfg.grace_days === g ? "border-tenant-primary bg-tenant-primary/10 font-medium text-tenant-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {g} days
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function BillingPanel({ plans }) {
  const [overview, setOverview] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [o, s] = await Promise.all([api.get("/platform/billing/overview"), api.get("/platform/billing/subscriptions")]);
    setOverview(o.data); setSubs(s.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const changePlan = async (tenantId, planId) => {
    try { await api.patch(`/platform/billing/tenants/${tenantId}/plan`, { plan_id: planId }); toast.success("Plan changed & entitlements re-provisioned"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setStatus = async (tenantId, status) => {
    if ((status === "suspended" || status === "cancelled") && !window.confirm(`This will block the operator's logins immediately. Continue?`)) return;
    try { await api.patch(`/platform/billing/tenants/${tenantId}/status`, { status }); toast.success(`Subscription ${status}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const overdueSubs = subs.filter((s) => s.has_overdue);

  return (
    <div className="space-y-8">
      {overdueSubs.length > 0 && (
        <div data-testid="overdue-banner" className="flex items-center gap-2 rounded-tenant-card border border-status-overdue/30 bg-status-overdue/10 px-4 py-3 text-sm text-status-overdue">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {overdueSubs.length} operator{overdueSubs.length > 1 ? "s have" : " has"} overdue invoices: {overdueSubs.map((s) => s.name).join(", ")}.
        </div>
      )}

      <DunningCard />
      <AutoInvoiceCard />
      <PaymentConfigCard />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={TrendingUp} label="MRR" value={inr(overview.mrr)} accent="text-status-available" />
        <Stat icon={AlertCircle} label="Outstanding" value={inr(overview.outstanding)} accent={overview.outstanding ? "text-status-overdue" : ""} />
        <Stat icon={IndianRupee} label="Collected" value={inr(overview.collected)} />
        <Stat icon={CheckCircle2} label="Active subs" value={overview.active_subscriptions} />
      </div>

      <div className="space-y-4">
        {subs.map((s) => (
          <div key={s.tenant_id} data-testid={`sub-row-${s.subdomain}`} className="rounded-tenant-card border border-border bg-tenant-surface p-5 shadow-tenant-soft">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold">{s.name}</p>
                  <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS[s.status]}`}>{s.status}</span>
                  {s.has_overdue && <span className="rounded-full bg-status-recovery/15 px-2 py-0.5 text-xs font-medium text-status-recovery">invoice overdue</span>}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {s.usage.vehicles}/{s.usage.vehicle_limit} vehicles{s.usage.vehicle_overage ? ` (+${s.usage.vehicle_overage} over)` : ""} · {s.usage.users}/{s.usage.user_limit} seats{s.usage.user_overage ? ` (+${s.usage.user_overage} over)` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-semibold">{inr(s.monthly_total)}<span className="text-sm font-normal text-muted-foreground">/mo</span></p>
                {s.outstanding > 0 && <p className="text-xs text-status-overdue">{inr(s.outstanding)} outstanding</p>}
                {s.next_invoice_at && <p className="text-xs text-muted-foreground">next invoice {new Date(s.next_invoice_at).toLocaleDateString()}</p>}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
              <select data-testid={`plan-select-${s.subdomain}`} value={s.plan_id} onChange={(e) => changePlan(s.tenant_id, e.target.value)}
                className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm">
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name} · {inr(p.price_inr)}/mo</option>)}
              </select>
              <InvoicesDialog sub={s} onChange={load} />
              {(s.status === "active" || s.status === "past_due") && (
                <Button data-testid={`suspend-${s.subdomain}`} size="sm" variant="outline" className="gap-1.5 text-status-recovery" onClick={() => setStatus(s.tenant_id, "suspended")}><Power className="h-3.5 w-3.5" /> Suspend</Button>
              )}
              {(s.status === "suspended" || s.status === "cancelled" || s.status === "past_due") && (
                <Button data-testid={`reactivate-${s.subdomain}`} size="sm" variant="outline" className="gap-1.5 text-status-available" onClick={() => setStatus(s.tenant_id, "active")}><PlayCircle className="h-3.5 w-3.5" /> Reactivate</Button>
              )}
              {s.status !== "cancelled" && (
                <Button data-testid={`cancel-${s.subdomain}`} size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => setStatus(s.tenant_id, "cancelled")}><Ban className="h-3.5 w-3.5" /> Cancel</Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
